import type { ApiErrorPayload, Role, SheetCellDetail, SheetView } from "./types";
import { apiErrorMessage, authHeaderForContext, buildQuery, shouldAttachJsonContentType, triggerBrowserDownload } from "./api-core";

const state = {
  role: localStorage.getItem("budgetRole") || "finance_admin",
  user: localStorage.getItem("budgetUser") || "",
  sessionAuth: localStorage.getItem("budgetSessionAuth") === "1"
};

export function setApiContext(next: { role?: string; user?: string; sessionAuth?: boolean }) {
  if (next.role !== undefined) {
    state.role = next.role;
    localStorage.setItem("budgetRole", next.role);
  }
  if (next.user !== undefined) {
    state.user = next.user;
    if (next.user) localStorage.setItem("budgetUser", next.user);
    else localStorage.removeItem("budgetUser");
  }
  if (next.sessionAuth !== undefined) {
    state.sessionAuth = next.sessionAuth;
    if (next.sessionAuth) localStorage.setItem("budgetSessionAuth", "1");
    else localStorage.removeItem("budgetSessionAuth");
  }
}

export function getApiContext() {
  return { ...state };
}

function authHeaders(): Record<string, string> {
  return authHeaderForContext(state);
}

export class BudgetApiError extends Error {
  status: number;
  payload: ApiErrorPayload;

  constructor(status: number, payload: ApiErrorPayload) {
    super(apiErrorMessage(status, payload));
    this.status = status;
    this.payload = payload;
  }
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  for (const [key, value] of Object.entries(authHeaders())) headers.set(key, value);
  if (shouldAttachJsonContentType(options.body) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(path, { ...options, headers, credentials: "same-origin" });
  if (!response.ok) {
    let payload: ApiErrorPayload = {};
    try {
      payload = await response.json();
    } catch {
      payload = { error: response.statusText };
    }
    throw new BudgetApiError(response.status, payload);
  }
  const contentType = response.headers.get("Content-Type") || "";
  if (!contentType.includes("application/json")) return (await response.blob()) as T;
  return response.json() as Promise<T>;
}

export async function login(code: string, password: string) {
  const result = await api<{ ok: boolean; actor: unknown; expires_at: string }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ code, password })
  });
  setApiContext({ sessionAuth: true, user: "" });
  return result;
}

export async function logout() {
  await api<{ ok: boolean }>("/api/auth/logout", { method: "POST", body: JSON.stringify({}) });
  setApiContext({ sessionAuth: true, user: "" });
}

export function toQuery(params: Record<string, string | number | null | undefined>) {
  return buildQuery(params);
}

export function getSheetViews() {
  return api<SheetView[]>("/api/sheet-views");
}

export function getSheetView(viewId: string) {
  return api<SheetView>(`/api/sheet-views/${encodeURIComponent(viewId)}`);
}

export function getSheetCells(viewId: string, params: Record<string, string | number | null | undefined>) {
  return api<{ view_id: string; tab_id: string; range: string; cells: any[] }>(`/api/sheet-views/${encodeURIComponent(viewId)}/cells${toQuery(params)}`);
}

export function getSheetCellDetail(viewId: string, address: string, params: Record<string, string | number | null | undefined>) {
  return api<SheetCellDetail>(`/api/sheet-views/${encodeURIComponent(viewId)}/cells/${encodeURIComponent(address)}${toQuery(params)}`);
}

export function runSheetCellAction(viewId: string, address: string, params: Record<string, string | number | null | undefined>, payload: Record<string, unknown>) {
  return api<Record<string, unknown>>(`/api/sheet-views/${encodeURIComponent(viewId)}/cells/${encodeURIComponent(address)}/action${toQuery(params)}`, { method: "POST", body: JSON.stringify(payload) });
}

export async function downloadFile(path: string, filename: string) {
  const headers = new Headers(authHeaders());
  const response = await fetch(path, { headers });
  if (!response.ok) {
    throw new BudgetApiError(response.status, { error: await response.text() });
  }
  return triggerBrowserDownload(path, filename, response, { document, URL });
}

export const ROLES: Role[] = [
  "finance_admin",
  "finance_controller",
  "cost_center_owner",
  "business_initiator",
  "functional_owner",
  "approver",
  "observer"
];
